/**
 * Response from /api/customer-documents/requests endpoint
 * Always returns an array, even when filtered by order_id
 */
export type CustomerDocumentRequestResponse = {
  id: number | string; // Can be string for large IDs that exceed Number.MAX_SAFE_INTEGER
  external_references: ExternalReferenceResponse[];
  document_request_templates: DocumentRequestTemplateResponse[];
  customer_document_approvals: CustomerDocumentApprovalResponse[] | null;
};

export type ExternalReferenceResponse = {
  id: number | string;
  external_id: string;
  external_type: string;
  source: string;
};

export type DocumentRequestTemplateResponse = {
  id: number | string;
  description: string;
  name: string;
  /** Use for CMS/Contentful lookup when present */
  content_key?: string;
};

export type CustomerDocumentApprovalResponse = {
  id: number | string;
  document_templates: DocumentTemplateResponse[];
  customer_documents: CustomerDocumentResponse[];
  status: string; // API returns lowercase string: "pending", "submitted", "approved", etc.
};

export type DocumentTemplateResponse = {
  id: number | string;
  description: string;
  name: string;
  required_count: number;
  /** Max documents allowed for this template. If absent, no limit. */
  maximum_count?: number;
  supported_content_types: string[];
  /** Use for CMS/Contentful lookup when present */
  content_key?: string;
};

export type CustomerDocumentResponse = {
  id: number | string;
  content_type: string;
  filename: string;
};

export enum DocumentApprovalStatus {
  PENDING = 'pending',
  AWAITING_SUBMISSION = 'awaiting_submission',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
  SUBMITTED = 'submitted',
}

/** Statuses that require the user to upload/submit documents (show in upload form and menu notification). SUBMITTED = blocked. */
export const APPROVAL_STATUSES_REQUIRING_UPLOAD: DocumentApprovalStatus[] = [
  DocumentApprovalStatus.PENDING, // uploaded but not submitted yet => can delete/re-upload
  DocumentApprovalStatus.AWAITING_SUBMISSION,
];

export const isApprovalAwaitingUpload = (status: DocumentApprovalStatus): boolean =>
  APPROVAL_STATUSES_REQUIRING_UPLOAD.includes(status);

export const isApprovalRequiringAction = (status: string): boolean =>
  status === DocumentApprovalStatus.PENDING || status === DocumentApprovalStatus.AWAITING_SUBMISSION;

export type ListDocumentRequestsParams = {
  limit?: number;
  offset?: number;
  order_id?: string;
};

export type SubmitDocumentParams = {
  file?: File;
  existing_document_id?: string;
  content_type?: string;
};
